'use client';

import { mergeProps, useObjectRef } from '@react-aria/utils';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import type { CommonProps } from '../types.js';
import { useColumnData } from './contexts/column-collection.js';
import { useTableRowState } from './contexts/row-state.js';
import { useTableCell } from './hooks/use-table-cell.js';

export interface TableBodyCellProps extends CommonProps {
  /** The contents of the cell. */
  children: ReactNode;
  /**
   * A unique identifier for the column that this cell belongs to.
   * Cells that belong to the same column must have the same `columnKey`.
   */
  columnKey: string;
}

function TableBodyCell(
  { children, UNSAFE_className, UNSAFE_style, columnKey, ...props }: TableBodyCellProps,
  ref: ForwardedRef<HTMLTableCellElement>,
) {
  const cellRef = useObjectRef(ref);
  const { rowKey } = useTableRowState();
  const { cellProps } = useTableCell(cellRef, rowKey, columnKey);
  const columnData = useColumnData(columnKey);

  return (
    <td
      {...mergeProps(props, cellProps)}
      ref={cellRef}
      className={UNSAFE_className}
      style={columnData?.width !== undefined ? { ...UNSAFE_style, width: columnData.width } : UNSAFE_style}
      aria-colindex={columnData?.number}
      data-alignment={columnData?.contentAlignment}
    >
      {children}
    </td>
  );
}

/**
 * Renders a cell within a table body row.
 *
 * See [table usage guidelines](https://ui.cimpress.io/components/table/) and [table building guide](https://ui.cimpress.io/dev-guides/tables/).
 */
const _TableBodyCell = forwardRef(TableBodyCell, 'TableBodyCell');

export { _TableBodyCell as TableBodyCell };
